<template>
  <div class="warehouse-table-wrapper">
    <!-- Barra horizontal sticky inferior (espejo) -->
    <div
      ref="bottomScroll"
      class="bottom-scrollbar no-print"
    >
      <div :style="{ width: tableWidth + 'px' }" />
    </div>

    <!-- Contenedor principal con scroll vertical + horizontal real -->
    <div
      ref="tableScroll"
      class="table-scroll"
      @scroll="syncBottomScrollbar"
    >
      <b-table
        ref="btable"
        :items="mapped"
        :fields="fields"
        small
        responsive="false"
        hover
        head-variant="light"
        :busy="loading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        show-empty
        @sort-changed="$emit('sort-changed', { sortBy, sortDesc })"
      >
        <!-- FOLIO -->
        <template #cell(folio)="d">
          <span v-if="d.item.showFolio">
            <router-link
              :to="`/orden-de-compra/${d.item.quote_id}`"
              class="text-primary font-weight-bolder"
            >
              {{ d.item.folio }}
            </router-link>
          </span>
        </template>

        <!-- CONCEPTO -->
        <template #cell(concept)="d">
          <span :title="d.item.concept">{{ d.item.concept }}</span>
        </template>

        <!-- UN -->
        <template #cell(unit)="d">
          <span>
            {{ formatUnit(d.item.unit, d.item.quantity) }}
          </span>
        </template>

        <!-- CANTIDAD -->
        <template #cell(quantity)="d">
          <span>
            {{ formatQty(d.item.quantity) }}
          </span>
        </template>

        <!-- PROVEEDOR -->
        <template #cell(supplier_name)="d">
          <span v-if="!d.item.hideSupplier">
            {{ d.item.supplier_name }}
          </span>
        </template>

        <template #cell(supplier_payment_date)="d">
          <span v-if="!d.item.hideSupplier">
            {{ d.item.supplier_payment_date ? formatDate(d.item.supplier_payment_date) : '—' }}
          </span>
        </template>

        <template #cell(supplier_unit_price)="d">
          <span>
            {{ money(d.item.supplier_unit_price) }}
          </span>
        </template>
        <template #cell(supplier_subtotal)="d">
          <span>
            {{ money(d.item.supplier_subtotal) }}
          </span>
        </template>
        <template #cell(supplier_iva)="d">
          <span>
            {{ money(d.item.supplier_iva) }}
          </span>
        </template>
        <!-- IMPORTE proveedor (antes TOTAL) -->
        <template #cell(supplier_importe)="d">
          <span>
            {{ money(d.item.supplier_total) }}
          </span>
        </template>

        <template #cell(supplier_quote_total)="d">
          <span v-if="!d.item.hideSupplierQuoteTotal">
            {{ money(d.item.supplier_quote_total) }}
          </span>
        </template>

        <!-- CLIENTE -->
        <template #cell(client_name)="d">
          <span v-if="!d.item.hideClientName">
            {{ d.item.client_name }}
          </span>
        </template>

        <template #cell(customer_payment_date)="d">
          <span v-if="!d.item.hideCustomerPaymentDate">
            {{ formatDate(d.item.customer_payment_date) }}
          </span>
        </template>

        <template #cell(customer_unit_price)="d">
          <span>
            {{ money(d.item.customer_unit_price) }}
          </span>
        </template>
        <template #cell(customer_subtotal)="d">
          <span>
            {{ money(d.item.customer_subtotal) }}
          </span>
        </template>
        <template #cell(customer_iva)="d">
          <span>
            {{ money(d.item.customer_iva) }}
          </span>
        </template>
        <!-- IMPORTE cliente (antes TOTAL) -->
        <template #cell(customer_importe)="d">
          <span>
            {{ money(d.item.customer_total) }}
          </span>
        </template>

        <!-- TOTAL por folio (suma de importes) -->
        <template #cell(quote_total)="d">
          <span v-if="!d.item.hideQuoteTotal">
            {{ money(d.item.quote_total) }}
          </span>
        </template>

        <!-- COMISIÓN / ACUMULADO -->
        <template #cell(commission)="d">
          <span>
            {{ money(d.item.commission) }}
          </span>
        </template>
        <template #cell(accumulated)="d">
          <span>
            {{ money(d.item.accumulated) }}
          </span>
        </template>

        <!-- CABECERAS compuestas -->
        <template #thead-top>
          <tr>
            <!-- 4 celdas fijas arriba de las columnas generales -->
            <th class="sticky-col-1 header-top-general" />
            <th class="sticky-col-2 header-top-general" />
            <th class="sticky-col-3 header-top-general" />
            <th class="sticky-col-4 header-top-general" />

            <!-- Grupos -->
            <th
              colspan="8"
              class="text-center bg-success text-white group-supplier header-top-supplier"
            >
              PROVEEDOR
            </th>
            <th
              colspan="8"
              class="text-center bg-primary text-white group-customer header-top-customer"
            >
              CLIENTE
            </th>
            <th
              colspan="2"
              class="text-center bg-light group-margins header-top-margins"
            >
              MÁRGENES
            </th>
          </tr>
        </template>

        <template #empty>
          <div class="text-center text-muted my-2">
            Sin datos
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'WarehouseTable',
  props: {
    rows: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    money: { type: Function, required: true },
    formatDate: { type: Function, required: true },
  },
  data() {
    return {
      sortBy: 'group_key',
      sortDesc: false,
      fields: [
        // Generales (sticky)
        {
          key: 'folio',
          label: 'FOLIO',
          sortable: true,
          thStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
          thClass: 'sticky-col-1 group-general',
          tdClass: 'sticky-col-1 group-general',
        },
        {
          key: 'concept',
          label: 'CONCEPTO',
          sortable: true,
          thStyle: { width: '240px', minWidth: '240px', maxWidth: '240px' },
          thClass: 'sticky-col-2 group-general',
          tdClass: 'sticky-col-2 group-general',
        },
        {
          key: 'unit',
          label: 'UN',
          thStyle: { width: '90px', minWidth: '90px', maxWidth: '90px' },
          thClass: 'sticky-col-3 group-general',
          tdClass: 'sticky-col-3 group-general',
        },
        {
          key: 'quantity',
          label: 'CANTIDAD',
          sortable: true,
          class: 'text-right',
          thStyle: { width: '110px', minWidth: '110px', maxWidth: '110px' },
          thClass: 'sticky-col-4 group-general',
          tdClass: 'sticky-col-4 group-general',
        },
        // Proveedor
        {
          key: 'supplier_name',
          label: 'PROVEEDOR',
          sortable: true,
          thStyle: { minWidth: '180px' },
          thClass: 'group-supplier',
          tdClass: 'group-supplier',
          tdAttr: (value, key, item) => (item.rowspanSupplier
            ? { rowspan: item.rowspanSupplier }
            : { style: item.hideSupplier ? 'display:none' : '' }),
        },
        {
          key: 'supplier_payment_date',
          label: 'FECHA PAGO',
          thClass: 'group-supplier',
          thStyle: { minWidth: '120px' },

          // 👇 Igual que supplier_name: rowspan o esconder subfilas
          tdAttr: (value, key, item) => (item.rowspanSupplier
            ? { rowspan: item.rowspanSupplier }
            : { style: item.hideSupplier ? 'display:none' : '' }),

          // 👇 Solo fluorescente si tiene crédito y NO tiene fecha
          tdClass: (value, key, item) => {
            const base = 'group-supplier'
            if (item.hideSupplier) return base

            const needsHighlight = !!item.supplier_has_credit && !item.supplier_payment_date

            return needsHighlight ? `${base} missing-pay-date` : base
          },
        },
        {
          key: 'supplier_invoice', label: 'FACTURA PROV', thClass: 'group-supplier', tdClass: 'group-supplier', thStyle: { minWidth: '130px' },
        },
        {
          key: 'supplier_unit_price', label: 'P.U.', class: 'text-right', thClass: 'group-supplier', tdClass: 'group-supplier', thStyle: { minWidth: '90px' },
        },
        {
          key: 'supplier_subtotal', label: 'SUBTOTAL', class: 'text-right', thClass: 'group-supplier', tdClass: 'group-supplier', thStyle: { minWidth: '100px' },
        },
        {
          key: 'supplier_iva', label: 'IVA', class: 'text-right', thClass: 'group-supplier', tdClass: 'group-supplier', thStyle: { minWidth: '90px' },
        },
        {
          key: 'supplier_importe', label: 'IMPORTE', class: 'text-right', thClass: 'group-supplier', tdClass: 'group-supplier', thStyle: { minWidth: '110px' },
        },
        {
          key: 'supplier_quote_total',
          label: 'TOTAL',
          class: 'text-right font-weight-bolder',
          thClass: 'group-supplier',
          tdClass: 'group-supplier',
          thStyle: { minWidth: '120px' },

          // rowspan o esconder subfilas (igual patrón)
          tdAttr: (value, key, item) => (item.rowspanSupplierQuoteTotal
            ? { rowspan: item.rowspanSupplierQuoteTotal }
            : { style: item.hideSupplierQuoteTotal ? 'display:none' : '' }),
        },

        // Cliente
        {
          key: 'client_name',
          label: 'CLIENTE',
          sortable: true,
          thClass: 'group-customer',
          tdClass: 'group-customer',
          thStyle: { minWidth: '180px' },

          tdAttr: (value, key, item) => (
            item.rowspanClientName
              ? { rowspan: item.rowspanClientName }
              : { style: item.hideClientName ? 'display:none' : '' }
          ),
        },
        {
          key: 'customer_payment_date',
          label: 'FECHA PAGO',
          thClass: 'group-customer',
          tdClass: 'group-customer',
          thStyle: { minWidth: '120px' },

          tdAttr: (value, key, item) => (
            item.rowspanCustomerPaymentDate
              ? { rowspan: item.rowspanCustomerPaymentDate }
              : { style: item.hideCustomerPaymentDate ? 'display:none' : '' }
          ),
        },
        {
          key: 'customer_invoice', label: 'FACTURA CLIENTE', thClass: 'group-customer', tdClass: 'group-customer', thStyle: { minWidth: '140px' },
        },
        {
          key: 'customer_unit_price', label: 'P.U.', class: 'text-right', thClass: 'group-customer', tdClass: 'group-customer', thStyle: { minWidth: '90px' },
        },
        {
          key: 'customer_subtotal', label: 'SUBTOTAL', class: 'text-right', thClass: 'group-customer', tdClass: 'group-customer', thStyle: { minWidth: '100px' },
        },
        {
          key: 'customer_iva', label: 'IVA', class: 'text-right', thClass: 'group-customer', tdClass: 'group-customer', thStyle: { minWidth: '90px' },
        },
        {
          key: 'customer_importe', label: 'IMPORTE', class: 'text-right', thClass: 'group-customer', tdClass: 'group-customer', thStyle: { minWidth: '110px' },
        },

        // Total folio
        {
          key: 'quote_total',
          label: 'TOTAL',
          class: 'text-right font-weight-bolder',
          thClass: 'group-total',
          tdClass: 'group-total',
          thStyle: { minWidth: '120px' },

          tdAttr: (value, key, item) => (item.rowspanQuoteTotal
            ? { rowspan: item.rowspanQuoteTotal }
            : { style: item.hideQuoteTotal ? 'display:none' : '' }),
        },

        // Márgenes
        {
          key: 'commission', label: 'COMISIÓN', class: 'text-right', thClass: 'group-margins', tdClass: 'group-margins', thStyle: { minWidth: '100px' },
        },
        {
          key: 'accumulated', label: 'ACUMULADO', class: 'text-right', thClass: 'group-margins', tdClass: 'group-margins', thStyle: { minWidth: '120px' },
        },
      ],
      tableWidth: 0,
      onBottomScroll: null,
    }
  },
  computed: {
    ...mapGetters('app', ['unitsFilter']),

    mapped() {
      const src = (this.rows || []).map(r => ({
        ...r,
        client_name: r.client_name || r.customer_name,
      }))

      const groups = new Map()
      src.forEach(r => {
        const key = r.group_key || r.folio
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(r)
      })

      const out = []

      groups.forEach((items, key) => {
        // Totales por cotización/folio
        const supplierQuoteTotal = items.reduce((acc, it) => acc + Number(it.supplier_total || 0), 0)
        const customerQuoteTotal = items.reduce((acc, it) => acc + Number(it.customer_total || 0), 0)

        const groupRows = []

        let i = 0
        while (i < items.length) {
          const it = items[i]
          const isFreight = (it.concept || '').toLowerCase() === 'flete'

          if (isFreight) {
            groupRows.push({
              ...it,
              showFolio: !it.is_subrow,
              rowspanSupplier: null,
              hideSupplier: false,
            })
            i += 1
          } else {
            const currentSupplier = it.supplier_name || '__nil__'

            let j = i
            while (
              j < items.length
        && (items[j].concept || '').toLowerCase() !== 'flete'
        && (items[j].supplier_name || '__nil__') === currentSupplier
            ) j += 1

            const span = j - i

            groupRows.push({
              ...it,
              showFolio: !it.is_subrow,
              rowspanSupplier: span > 1 ? span : null,
              hideSupplier: false,
            })

            for (let k = i + 1; k < j; k += 1) {
              groupRows.push({
                ...items[k],
                showFolio: !items[k].is_subrow,
                rowspanSupplier: null,
                hideSupplier: true,
              })
            }

            i = j
          }
        }

        const spanAll = groupRows.length || 1

        groupRows.forEach((r, idx) => {
          r.group_key = key

          if (idx === 0) {
            r.supplier_quote_total = supplierQuoteTotal
            r.rowspanSupplierQuoteTotal = spanAll
            r.hideSupplierQuoteTotal = false

            r.quote_total = customerQuoteTotal
            r.rowspanQuoteTotal = spanAll
            r.hideQuoteTotal = false

            // ✅ FECHA PAGO CLIENTE
            r.rowspanCustomerPaymentDate = spanAll
            r.hideCustomerPaymentDate = false

            // ✅ CLIENTE (rowspan)
            r.rowspanClientName = spanAll
            r.hideClientName = false
          } else {
            r.rowspanSupplierQuoteTotal = null
            r.hideSupplierQuoteTotal = true

            r.rowspanQuoteTotal = null
            r.hideQuoteTotal = true

            // ✅ FECHA PAGO CLIENTE
            r.rowspanCustomerPaymentDate = null
            r.hideCustomerPaymentDate = true

            // ✅ CLIENTE (rowspan)
            r.rowspanClientName = null
            r.hideClientName = true
          }

          out.push(r)
        })
      })

      return out
    },

  },
  watch: {
    rows() {
      this.$nextTick(this.calcTableWidth)
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.calcTableWidth()
      this.initBottomScrollbarSync()
      window.addEventListener('resize', this.calcTableWidth)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableWidth)

    const bottom = this.$refs.bottomScroll
    if (bottom && this.onBottomScroll) {
      bottom.removeEventListener('scroll', this.onBottomScroll)
    }
  },
  methods: {
    getExcelTableHtml() {
      const columnGroups = [
        { label: '', colspan: 4, className: 'header-top-general' },
        { label: 'PROVEEDOR', colspan: 8, className: 'header-top-supplier' },
        { label: 'CLIENTE', colspan: 8, className: 'header-top-customer' },
        { label: 'MÁRGENES', colspan: 2, className: 'header-top-margins' },
      ]

      const exportFields = [
        {
          key: 'folio', label: 'FOLIO', className: 'group-general text-center', value: item => (item.showFolio ? item.folio : ''),
        },
        {
          key: 'concept', label: 'CONCEPTO', className: 'group-general', value: item => item.concept || '',
        },
        {
          key: 'unit', label: 'UN', className: 'group-general text-center', value: item => this.formatUnit(item.unit, item.quantity),
        },
        {
          key: 'quantity', label: 'CANTIDAD', className: 'group-general text-right', value: item => this.formatQty(item.quantity),
        },

        {
          key: 'supplier_name', label: 'PROVEEDOR', className: 'group-supplier', rowspanKey: 'rowspanSupplier', hideKey: 'hideSupplier', value: item => item.supplier_name || '—',
        },
        {
          key: 'supplier_payment_date', label: 'FECHA PAGO', className: 'group-supplier text-center', rowspanKey: 'rowspanSupplier', hideKey: 'hideSupplier', value: item => (item.supplier_payment_date ? this.formatDate(item.supplier_payment_date) : '—'),
        },
        {
          key: 'supplier_invoice', label: 'FACTURA PROV', className: 'group-supplier', value: item => item.supplier_invoice || '—',
        },
        {
          key: 'supplier_unit_price', label: 'P.U.', className: 'group-supplier text-right', value: item => this.money(item.supplier_unit_price),
        },
        {
          key: 'supplier_subtotal', label: 'SUBTOTAL', className: 'group-supplier text-right', value: item => this.money(item.supplier_subtotal),
        },
        {
          key: 'supplier_iva', label: 'IVA', className: 'group-supplier text-right', value: item => this.money(item.supplier_iva),
        },
        {
          key: 'supplier_importe', label: 'IMPORTE', className: 'group-supplier text-right', value: item => this.money(item.supplier_total),
        },
        {
          key: 'supplier_quote_total', label: 'TOTAL', className: 'group-supplier text-right font-weight-bolder', rowspanKey: 'rowspanSupplierQuoteTotal', hideKey: 'hideSupplierQuoteTotal', value: item => this.money(item.supplier_quote_total),
        },

        {
          key: 'client_name', label: 'CLIENTE', className: 'group-customer', rowspanKey: 'rowspanClientName', hideKey: 'hideClientName', value: item => item.client_name || item.customer_name || '—',
        },
        {
          key: 'customer_payment_date', label: 'FECHA PAGO', className: 'group-customer text-center', rowspanKey: 'rowspanCustomerPaymentDate', hideKey: 'hideCustomerPaymentDate', value: item => (item.customer_payment_date ? this.formatDate(item.customer_payment_date) : '—'),
        },
        {
          key: 'customer_invoice', label: 'FACTURA CLIENTE', className: 'group-customer', value: item => item.customer_invoice || '—',
        },
        {
          key: 'customer_unit_price', label: 'P.U.', className: 'group-customer text-right', value: item => this.money(item.customer_unit_price),
        },
        {
          key: 'customer_subtotal', label: 'SUBTOTAL', className: 'group-customer text-right', value: item => this.money(item.customer_subtotal),
        },
        {
          key: 'customer_iva', label: 'IVA', className: 'group-customer text-right', value: item => this.money(item.customer_iva),
        },
        {
          key: 'customer_importe', label: 'IMPORTE', className: 'group-customer text-right', value: item => this.money(item.customer_total),
        },
        {
          key: 'quote_total', label: 'TOTAL', className: 'group-total text-right font-weight-bolder', rowspanKey: 'rowspanQuoteTotal', hideKey: 'hideQuoteTotal', value: item => this.money(item.quote_total),
        },

        {
          key: 'commission', label: 'COMISIÓN', className: 'group-margins text-right', value: item => this.money(item.commission),
        },
        {
          key: 'accumulated', label: 'ACUMULADO', className: 'group-margins text-right', value: item => this.money(item.accumulated),
        },
      ]

      const theadTop = `
        <tr>
          ${columnGroups.map(group => `
            <th colspan="${group.colspan}" class="${group.className}">
              ${group.label}
            </th>
          `).join('')}
        </tr>
      `

      const thead = `
        <tr>
          ${exportFields.map(field => `
            <th class="${field.className}">
              ${field.label}
            </th>
          `).join('')}
        </tr>
      `

      const tbody = this.mapped.map(item => {
        const cells = exportFields.map(field => {
          if (field.hideKey && item[field.hideKey]) return ''

          const rowspan = field.rowspanKey ? item[field.rowspanKey] : null
          const attrs = [
            field.className ? `class="${field.className}"` : '',
            rowspan ? `rowspan="${rowspan}"` : '',
          ].filter(Boolean).join(' ')

          return `<td ${attrs}>${field.value(item)}</td>`
        }).join('')

        return `<tr>${cells}</tr>`
      }).join('')

      return `
        <style>
          .warehouse-export-table {
            width: 100%;
          }
          .warehouse-export-table th,
          .warehouse-export-table td {
            border: 1px solid #dfe3e8;
            padding: 6px 8px;
            font-size: 12px;
            white-space: nowrap;
            vertical-align: top;
          }
          .warehouse-export-table thead th {
            font-weight: 700;
            text-align: center;
          }
          .warehouse-export-table .group-general {
            background: #fcfcfd;
          }
          .warehouse-export-table .group-supplier {
            background: #f6fbf8;
          }
          .warehouse-export-table .group-customer {
            background: #f6f8fd;
          }
          .warehouse-export-table .group-margins {
            background: #fafafa;
          }
          .warehouse-export-table .group-total {
            background: #fff7ed;
          }
          .warehouse-export-table .header-top-general {
            background: #ffffff;
          }
          .warehouse-export-table .header-top-supplier {
            background: #28c76f;
            color: #ffffff;
          }
          .warehouse-export-table .header-top-customer {
            background: #7367f0;
            color: #ffffff;
          }
          .warehouse-export-table .header-top-margins {
            background: #f1f3f5;
          }
          .warehouse-export-table .text-right {
            text-align: right;
          }
          .warehouse-export-table .text-center {
            text-align: center;
          }
          .warehouse-export-table .font-weight-bolder {
            font-weight: 700;
          }
          .warehouse-export-table .text-primary {
            color: #7367f0;
          }
        </style>
        <table class="warehouse-export-table">
          <thead>
            ${theadTop}
            ${thead}
          </thead>
          <tbody>
            ${tbody}
          </tbody>
        </table>
      `
    },

    initBottomScrollbarSync() {
      const bottom = this.$refs.bottomScroll
      const main = this.$refs.tableScroll
      if (!bottom || !main) return

      this.onBottomScroll = () => {
        main.scrollLeft = bottom.scrollLeft
      }
      bottom.addEventListener('scroll', this.onBottomScroll)
    },

    formatQty(q) {
      const n = Number(q)
      if (Number.isNaN(n)) return q
      return Number.isInteger(n)
        ? n.toString()
        : n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 3 })
    },

    formatUnit(unitType, quantity) {
      if (!unitType) return ''
      return this.$tc(this.unitsFilter(unitType), quantity)
    },

    calcTableWidth() {
      const tableEl = this.$refs.btable?.$el?.querySelector('table')
      if (tableEl) this.tableWidth = tableEl.scrollWidth
    },

    syncBottomScrollbar() {
      const bottom = this.$refs.bottomScroll
      const main = this.$refs.tableScroll
      if (bottom && main) {
        bottom.scrollLeft = main.scrollLeft

        // 👇 agrega/quita clase para sombra cuando hay scroll horizontal
        if (main.scrollLeft > 0) {
          main.classList.add('scrolled-x')
        } else {
          main.classList.remove('scrolled-x')
        }
      }
    },
  },
}
</script>

<style scoped>
/* Wrapper general */
.warehouse-table-wrapper {
  position: relative;
}

/* Scroll vertical principal */
.table-scroll {
  max-height: calc(100vh - 220px); /* ajusta según tu layout */
  overflow: auto;
  border-radius: 6px;
}

/* Barra inferior sticky */
.bottom-scrollbar {
  position: sticky;
  bottom: 0;
  height: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  background: white;
  border-top: 1px solid #e9ecef;
  z-index: 10;
}

/* Compactar celdas + hacer que z-index aplique en tabla */
.table-scroll ::v-deep th,
.table-scroll ::v-deep td {
  padding: 0.25rem 0.45rem;
  white-space: nowrap;
  font-size: 12.5px;
  position: relative;  /* 👈 CLAVE: ahora z-index sí aplica */
  z-index: 1;          /* celdas normales abajo */
}

/* ===== STICKY HEADERS (2 filas) ===== */

/* Fila 1: barra de grupos (thead-top) */
.table-scroll ::v-deep thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 12;               /* arriba de todo */
  background: inherit;        /* respeta color de grupo */
}

/* Fila 2: nombres de columna */
.table-scroll ::v-deep thead tr:nth-child(2) th {
  position: sticky;
  top: 34px;                  /* altura de la fila de grupos */
  z-index: 11;
  background: #f8f9fa;        /* sólido para que no transparente */
}

/* ===== STICKY COLUMNAS IZQUIERDAS (ARRIBA DE TODO) ===== */
.table-scroll ::v-deep .sticky-col-1,
.table-scroll ::v-deep .sticky-col-2,
.table-scroll ::v-deep .sticky-col-3,
.table-scroll ::v-deep .sticky-col-4 {
  position: sticky;
  z-index: 20;          /* 👈 subimos capa para que ganen siempre */
  background: white;
  border-right: 1px solid #e5e7eb;
  box-shadow: 2px 0 0 rgba(0,0,0,0.03);
}

/* offsets (según minWidth que definiste) */
.table-scroll ::v-deep .sticky-col-1 { left: 0; }
.table-scroll ::v-deep .sticky-col-2 { left: 120px; }             /* folio */
.table-scroll ::v-deep .sticky-col-3 { left: 360px; }             /* 120 + 240 */
.table-scroll ::v-deep .sticky-col-4 { left: 450px; }             /* 120 + 240 + 90 */

/* Headers de esas columnas aún más arriba */
.table-scroll ::v-deep thead .sticky-col-1,
.table-scroll ::v-deep thead .sticky-col-2,
.table-scroll ::v-deep thead .sticky-col-3,
.table-scroll ::v-deep thead .sticky-col-4 {
  z-index: 21;
}

/* ✅ Colores neutros por grupo */
.table-scroll ::v-deep .group-general { background: #fcfcfd; }
.table-scroll ::v-deep .group-supplier { background: #f6fbf8; }
.table-scroll ::v-deep .group-customer { background: #f6f8fd; }
.table-scroll ::v-deep .group-margins  { background: #fafafa; }
.table-scroll ::v-deep .group-total    { background: #fff7ed; }

/* Header también con sus tonos */
.table-scroll ::v-deep thead .group-general { background: #f2f4f7 !important; }
.table-scroll ::v-deep thead .group-supplier { background: #e9f7ef !important; }
.table-scroll ::v-deep thead .group-customer { background: #e9eefc !important; }
.table-scroll ::v-deep thead .group-margins  { background: #f1f3f5 !important; }
.table-scroll ::v-deep thead .group-total    { background: #fdebd3 !important; }

/* =========================
   FIX: headers generales sticky siempre arriba
   ========================= */

/* 1) Baja la capa de TODOS los headers normales (proveedor/cliente/etc.) */
.table-scroll ::v-deep thead tr:nth-child(2) th {
  z-index: 10 !important;  /* debajo */
}

/* 2) Sube MUCHO la capa de los 4 headers sticky */
.table-scroll ::v-deep thead tr:nth-child(2) th.sticky-col-1,
.table-scroll ::v-deep thead tr:nth-child(2) th.sticky-col-2,
.table-scroll ::v-deep thead tr:nth-child(2) th.sticky-col-3,
.table-scroll ::v-deep thead tr:nth-child(2) th.sticky-col-4 {
  z-index: 100 !important;         /* arriba de todo */
  background: #f8f9fa !important;  /* sólido (no transparente) */
}

/* 3) Y por seguridad, que las celdas sticky del body también tapen */
.table-scroll ::v-deep td.sticky-col-1,
.table-scroll ::v-deep td.sticky-col-2,
.table-scroll ::v-deep td.sticky-col-3,
.table-scroll ::v-deep td.sticky-col-4 {
  z-index: 50 !important;
  background: #fff !important;
}

/* Las 4 celdas fijas de la izquierda arriba: neutras y NO tapan grupos */
.table-scroll ::v-deep thead tr:first-child th.header-top-general {
  position: sticky;
  top: 0;
  z-index: 200 !important;
  background: white !important;   /* neutro */
}

/* Reafirmar colores y capas de PROVEEDOR / CLIENTE / MÁRGENES */
.table-scroll ::v-deep thead tr:first-child th.header-top-supplier {
  background: #28c76f !important; /* mismo verde */
  color: white !important;
  z-index: 150 !important;
}

.table-scroll ::v-deep thead tr:first-child th.header-top-customer {
  background: #7367f0 !important; /* mismo morado/azul */
  color: white !important;
  z-index: 150 !important;
}

.table-scroll ::v-deep thead tr:first-child th.header-top-margins {
  background: #f1f3f5 !important;
  z-index: 150 !important;
}

/* =========================================
   FIX real: no bleed + no tapa primera fila
   ========================================= */

/* La tabla no debe colapsar bordes para sticky limpio */
.table-scroll ::v-deep table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

/* Fondo sólido para cualquier th sticky */
.table-scroll ::v-deep thead th {
  background-clip: padding-box;
}

/* ----- Fila 1: thead-top (grupos) ----- */
.table-scroll ::v-deep thead tr:first-child th {
  height: 32px;                       /* altura fija */
  border-bottom: 1px solid #e5e7eb;   /* corte limpio */
  /* NO ponemos background aquí para no matar colores */
}

/* ----- Fila 2: nombres de columnas ----- */
.table-scroll ::v-deep thead tr:nth-child(2) th {
  height: 32px;                       /* misma altura que fila 1 */
  top: 32px !important;               /* << CLAVE: pegada justo debajo */
  background: #f8f9fa !important;     /* sólido */
  border-bottom: 1px solid #e5e7eb;   /* corte limpio */
}

/* =========================
   1) Menos ruido de grid
   ========================= */
.table-scroll ::v-deep tbody td {
  border-top: 1px solid rgba(0,0,0,0.04) !important; /* más ligero */
}
.table-scroll ::v-deep td,
.table-scroll ::v-deep th {
  border-right: 1px solid rgba(0,0,0,0.05) !important; /* más sutil */
}

/* =========================
   2) Zebra suave por filas (ayuda seguimiento)
   ========================= */
.table-scroll ::v-deep tbody tr:nth-child(even):not(.group-total-row) td {
  background: rgba(0,0,0,0.012); /* casi imperceptible */
}

/* =========================
   3) Jerarquía de FOLIO y TOTAL
   ========================= */
.table-scroll ::v-deep td.sticky-col-1 {
  font-weight: 600;
  color: #3b2a6f; /* mismo tono que folio */
}

/* =========================
   4) Bloques proveedor/cliente más claros
   ========================= */
/* proveedor ligeramente distinto */
.table-scroll ::v-deep .group-supplier { background: #f3faf6 !important; }
/* cliente ligeramente distinto */
.table-scroll ::v-deep .group-customer { background: #f2f5fd !important; }
/* márgenes neutro */
.table-scroll ::v-deep .group-margins { background: #fafafa !important; }

/* headers de grupos más nítidos */
.table-scroll ::v-deep thead .group-supplier { background: #dff4e8 !important; }
.table-scroll ::v-deep thead .group-customer { background: #e1e8fb !important; }

/* =========================
   5) Importes clave (cliente) con más protagonismo
   ========================= */
.table-scroll ::v-deep td.group-customer.text-right {
  font-variant-numeric: tabular-nums;
}
.table-scroll ::v-deep td.group-customer:last-of-type { /* IMPORTE cliente */
  font-weight: 700;
  color: #111827;
}

/* =========================
   6) Concepto con ellipsis + tooltip-friendly
   ========================= */
.table-scroll ::v-deep td.sticky-col-2 {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* =========================
   7) Sombra sutil al panel sticky cuando hay scroll horizontal
   ========================= */
.table-scroll.scrolled-x ::v-deep .sticky-col-4 {
  box-shadow: 4px 0 6px rgba(0,0,0,0.06);
}

/* Celda fluorescente cuando falta fecha de pago proveedor con crédito */
.table-scroll ::v-deep td.missing-pay-date {
  background: #ccff00 !important;  /* fluorescente */
  color: #000 !important;
  font-weight: 800;
  box-shadow: inset 0 0 0 2px rgba(0,0,0,0.08);
}

</style>
